import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, ChevronDown, ChevronUp, Trash2, ToggleLeft, ToggleRight, Copy, FlaskConical, LogIn, Pencil } from 'lucide-react';
import {
    listCompanies,
    createCompany,
    toggleCompanyActive,
    updateCompany,
    deleteCompanyDryRun,
    deleteCompanyExecute,
    listAllUsers,
    toggleTestAccountFlag,
    impersonateTestAccount,
} from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

// Convert a company name to a slug: lowercase, spaces→hyphens, strip non-alphanumeric
function nameToSlug(name) {
    return name
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
}

// Convert an org number to a slug: strip non-alphanumeric, lowercase
function orgNumberToSlug(orgNumber) {
    return orgNumber.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

export default function CompaniesPage() {
    const { t } = useTranslation();

    // List state
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);

    // Create form
    const [createOpen, setCreateOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createForm, setCreateForm] = useState({
        name: '',
        slug: '',
        owner_email: '',
        owner_name: '',
        org_number: '',
    });
    const [createErrors, setCreateErrors] = useState({});

    // Temp password success dialog
    const [tempPasswordDialog, setTempPasswordDialog] = useState({ open: false, password: '', company: '' });
    const [tempPasswordCopied, setTempPasswordCopied] = useState(false);

    // Delete confirmation
    const [deleteState, setDeleteState] = useState({
        open: false,
        slug: '',
        name: '',
        dryRunResult: null,
        dryRunLoading: false,
        executing: false,
    });

    // Edit company dialog
    const [editState, setEditState] = useState({
        open: false,
        company: null, // the company being edited (snapshot at open time)
        form: {
            name: '',
            org_number: '',
            legal_address: '',
            vat_number: '',
            phone: '',
            email: '',
            website: '',
        },
        saving: false,
    });

    // Toggle in-progress tracking (slug → bool)
    const [togglingSlug, setTogglingSlug] = useState(null);

    // Test accounts state
    const [allUsers, setAllUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [togglingTestFlag, setTogglingTestFlag] = useState(null); // userId
    const [impersonating, setImpersonating] = useState(null); // userId

    const fetchCompanies = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listCompanies();
            setCompanies(res.data);
        } catch {
            toast.error(t('superadmin.load_error', 'Failed to load companies'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    const fetchAllUsers = useCallback(async () => {
        setUsersLoading(true);
        try {
            const res = await listAllUsers();
            setAllUsers(res.data);
        } catch {
            toast.error(t('superadmin.load_error', 'Failed to load users'));
        } finally {
            setUsersLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchAllUsers();
    }, [fetchAllUsers]);

    const handleToggleTestFlag = async (userId) => {
        setTogglingTestFlag(userId);
        try {
            await toggleTestAccountFlag(userId);
            await fetchAllUsers();
        } catch (err) {
            const msg = err?.response?.data?.error || t('common.error', 'An error occurred');
            toast.error(msg);
        } finally {
            setTogglingTestFlag(null);
        }
    };

    const handleImpersonate = async (userId) => {
        setImpersonating(userId);
        try {
            const res = await impersonateTestAccount(userId);
            // Save original super_admin token before switching
            localStorage.setItem('hislandtime.original_token', localStorage.getItem('token'));
            localStorage.setItem('hislandtime.original_user', localStorage.getItem('user'));
            // Switch to impersonation token
            localStorage.setItem('token', res.data.access_token);
            localStorage.setItem('user', JSON.stringify(res.data.impersonated_user));
            // Full reload to pick up new auth state
            window.location.href = '/dashboard';
        } catch (err) {
            const msg = err?.response?.data?.error || t('common.error', 'An error occurred');
            toast.error(msg);
            setImpersonating(null);
        }
    };

    // ── Create form helpers ───────────────────────────────────────────────────

    const handleCreateNameChange = (e) => {
        const name = e.target.value;
        setCreateForm((prev) => ({ ...prev, name }));
        setCreateErrors((prev) => ({ ...prev, name: undefined }));
    };

    const handleOrgNumberChange = (e) => {
        const val = e.target.value;
        setCreateForm((prev) => ({
            ...prev,
            org_number: val,
            slug: orgNumberToSlug(val),
        }));
        setCreateErrors((prev) => ({ ...prev, org_number: undefined, slug: undefined }));
    };

    const handleCreateFieldChange = (field) => (e) => {
        setCreateForm((prev) => ({ ...prev, [field]: e.target.value }));
        setCreateErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const validateCreate = () => {
        const errors = {};
        if (!createForm.name.trim()) errors.name = t('superadmin.field_required', 'Required');
        if (!createForm.org_number.trim()) errors.org_number = t('superadmin.field_required', 'Required');
        if (!createForm.slug.trim()) errors.slug = t('superadmin.field_required', 'Required');
        if (!createForm.owner_email.trim()) errors.owner_email = t('superadmin.field_required', 'Required');
        if (!createForm.owner_name.trim()) errors.owner_name = t('superadmin.field_required', 'Required');
        return errors;
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        const errors = validateCreate();
        if (Object.keys(errors).length > 0) {
            setCreateErrors(errors);
            return;
        }

        setCreating(true);
        try {
            const payload = {
                name: createForm.name.trim(),
                slug: createForm.slug.trim(),
                owner_email: createForm.owner_email.trim(),
                owner_name: createForm.owner_name.trim(),
                org_number: createForm.org_number.trim(),
            };
            const res = await createCompany(payload);
            const { temp_password, company } = res.data;
            setCreateOpen(false);
            setCreateForm({ name: '', slug: '', owner_email: '', owner_name: '', org_number: '' });
            setCreateErrors({});
            await fetchCompanies();
            setTempPasswordDialog({
                open: true,
                password: temp_password,
                company: company?.name || createForm.name,
            });
            setTempPasswordCopied(false);
        } catch (err) {
            const msg = err?.response?.data?.error || t('superadmin.create_error', 'Failed to create company');
            toast.error(msg);
        } finally {
            setCreating(false);
        }
    };

    const handleCreateCancel = () => {
        setCreateOpen(false);
        setCreateForm({ name: '', slug: '', owner_email: '', owner_name: '', org_number: '' });
        setCreateErrors({});
    };

    // ── Edit company ──────────────────────────────────────────────────────────

    const openEditDialog = (company) => {
        setEditState({
            open: true,
            company,
            form: {
                name: company.name || '',
                org_number: company.org_number || '',
                legal_address: company.legal_address || '',
                vat_number: company.vat_number || '',
                phone: company.phone || '',
                email: company.email || '',
                website: company.website || '',
            },
            saving: false,
        });
    };

    const handleEditFieldChange = (field) => (e) => {
        setEditState((prev) => ({
            ...prev,
            form: { ...prev.form, [field]: e.target.value },
        }));
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        const company = editState.company;
        if (!company) return;

        // Compute delta: only send fields that changed from the snapshot
        const snapshot = {
            name: company.name || '',
            org_number: company.org_number || '',
            legal_address: company.legal_address || '',
            vat_number: company.vat_number || '',
            phone: company.phone || '',
            email: company.email || '',
            website: company.website || '',
        };
        const changes = {};
        for (const key of Object.keys(snapshot)) {
            if (editState.form[key] !== snapshot[key]) {
                changes[key] = editState.form[key];
            }
        }

        if (Object.keys(changes).length === 0) {
            toast.info(t('superadmin.edit_no_changes', 'No fields were changed'));
            setEditState((prev) => ({ ...prev, open: false }));
            return;
        }

        setEditState((prev) => ({ ...prev, saving: true }));
        try {
            const res = await updateCompany(company.slug, changes);
            const updated = res.data;
            // Optimistic local state update
            setCompanies((prev) =>
                prev.map((c) => (c.slug === company.slug ? { ...c, ...updated } : c))
            );
            setEditState({ open: false, company: null, form: {}, saving: false });
            toast.success(t('superadmin.edit_success', 'Company updated'));
        } catch (err) {
            const msg = err?.response?.data?.detail || t('superadmin.edit_error', 'Failed to update company');
            toast.error(msg);
            setEditState((prev) => ({ ...prev, saving: false }));
        }
    };

    const handleEditCancel = () => {
        setEditState({ open: false, company: null, form: {}, saving: false });
    };

    // ── Toggle active ─────────────────────────────────────────────────────────

    const handleToggle = async (slug, name, currentlyActive) => {
        const label = currentlyActive
            ? t('superadmin.confirm_deactivate', `Deactivate "${name}"?`)
            : t('superadmin.confirm_activate', `Activate "${name}"?`);

        if (!window.confirm(label)) return;

        setTogglingSlug(slug);
        try {
            await toggleCompanyActive(slug);
            await fetchCompanies();
            toast.success(
                currentlyActive
                    ? t('superadmin.deactivated', 'Company deactivated')
                    : t('superadmin.activated', 'Company activated')
            );
        } catch (err) {
            const msg = err?.response?.data?.error || t('superadmin.toggle_error', 'Failed to toggle status');
            toast.error(msg);
        } finally {
            setTogglingSlug(null);
        }
    };

    // ── Delete flow ───────────────────────────────────────────────────────────

    const openDeleteDialog = async (slug, name) => {
        setDeleteState({ open: true, slug, name, dryRunResult: null, dryRunLoading: true, executing: false });
        try {
            const res = await deleteCompanyDryRun(slug);
            setDeleteState((prev) => ({ ...prev, dryRunResult: res.data, dryRunLoading: false }));
        } catch {
            setDeleteState((prev) => ({ ...prev, dryRunResult: null, dryRunLoading: false }));
            toast.error(t('superadmin.dry_run_error', 'Failed to check deletion impact'));
        }
    };

    const handleDeleteExecute = async () => {
        setDeleteState((prev) => ({ ...prev, executing: true }));
        try {
            await deleteCompanyExecute(deleteState.slug);
            setDeleteState({ open: false, slug: '', name: '', dryRunResult: null, dryRunLoading: false, executing: false });
            await fetchCompanies();
            toast.success(t('superadmin.delete_success', 'Company deleted'));
        } catch (err) {
            const msg = err?.response?.data?.error || t('superadmin.delete_error', 'Failed to delete company');
            toast.error(msg);
            setDeleteState((prev) => ({ ...prev, executing: false }));
        }
    };

    const handleDeleteCancel = () => {
        setDeleteState({ open: false, slug: '', name: '', dryRunResult: null, dryRunLoading: false, executing: false });
    };

    // ── Copy temp password ────────────────────────────────────────────────────

    const copyTempPassword = () => {
        navigator.clipboard.writeText(tempPasswordDialog.password).then(() => {
            setTempPasswordCopied(true);
        });
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="max-w-6xl mx-auto">
            {/* Page header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900">
                        {t('superadmin.companies', 'Companies')}
                    </h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {t('superadmin.companies_subtitle', 'All tenant companies on this platform')}
                    </p>
                </div>
                <Button
                    onClick={() => setCreateOpen(true)}
                    className="flex items-center gap-2"
                >
                    <Plus className="size-4" />
                    {t('superadmin.create_company', 'Create Company')}
                </Button>
            </div>

            {/* Company table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-sm text-gray-500">
                            {t('common.loading', 'Loading')}…
                        </div>
                    ) : companies.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-sm text-gray-500">
                            <p>{t('superadmin.no_companies', 'No companies yet.')}</p>
                            <button
                                onClick={() => setCreateOpen(true)}
                                className="mt-2 text-violet-600 hover:underline text-sm"
                            >
                                {t('superadmin.create_company', 'Create Company')}
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-200 bg-gray-50">
                                        <th className="text-left px-4 py-3 font-medium text-gray-600">
                                            {t('superadmin.company_name', 'Company Name')}
                                        </th>
                                        <th className="text-left px-4 py-3 font-medium text-gray-600">
                                            {t('superadmin.slug', 'Slug (ID)')}
                                        </th>
                                        <th className="text-left px-4 py-3 font-medium text-gray-600">
                                            {t('superadmin.owner_email', 'Owner Email')}
                                        </th>
                                        <th className="text-left px-4 py-3 font-medium text-gray-600">
                                            {t('superadmin.employees', 'Employees')}
                                        </th>
                                        <th className="text-left px-4 py-3 font-medium text-gray-600">
                                            {t('common.status', 'Status')}
                                        </th>
                                        <th className="text-left px-4 py-3 font-medium text-gray-600">
                                            {t('superadmin.created_at', 'Created')}
                                        </th>
                                        <th className="text-left px-4 py-3 font-medium text-gray-600">
                                            {t('common.actions', 'Actions')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {companies.map((company) => (
                                        <tr
                                            key={company.slug}
                                            className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                                        >
                                            <td className="px-4 py-3 font-medium text-gray-900">
                                                {company.name}
                                            </td>
                                            <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                                                {company.slug}
                                            </td>
                                            <td className="px-4 py-3 text-gray-600">
                                                {company.owner_email || '—'}
                                            </td>
                                            <td className="px-4 py-3 text-gray-600">
                                                {company.employee_count ?? '—'}
                                            </td>
                                            <td className="px-4 py-3">
                                                {company.is_active ? (
                                                    <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full font-medium">
                                                        {t('superadmin.active', 'Active')}
                                                    </span>
                                                ) : (
                                                    <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full font-medium">
                                                        {t('superadmin.inactive', 'Inactive')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-gray-500">
                                                {formatDate(company.created_at)}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() =>
                                                            handleToggle(
                                                                company.slug,
                                                                company.name,
                                                                company.is_active
                                                            )
                                                        }
                                                        disabled={togglingSlug === company.slug}
                                                        className="flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                                                        title={t('superadmin.toggle_active', 'Toggle Active')}
                                                    >
                                                        {company.is_active ? (
                                                            <ToggleRight className="size-3.5 text-green-600" />
                                                        ) : (
                                                            <ToggleLeft className="size-3.5 text-gray-400" />
                                                        )}
                                                        {togglingSlug === company.slug
                                                            ? t('common.updating', 'Updating…')
                                                            : t('superadmin.toggle_active', 'Toggle')}
                                                    </button>
                                                    <button
                                                        onClick={() => openEditDialog(company)}
                                                        className="flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-violet-200 text-violet-700 hover:bg-violet-50 transition-colors"
                                                        title={t('superadmin.edit_company', 'Edit Company')}
                                                    >
                                                        <Pencil className="size-3.5" />
                                                        {t('superadmin.edit_company', 'Edit')}
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            openDeleteDialog(company.slug, company.name)
                                                        }
                                                        className="flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                                                        title={t('superadmin.delete_company', 'Delete Company')}
                                                    >
                                                        <Trash2 className="size-3.5" />
                                                        {t('common.delete', 'Delete')}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create Company Dialog */}
            <Dialog open={createOpen} onOpenChange={(open) => { if (!open) handleCreateCancel(); }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t('superadmin.create_company', 'Create Company')}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateSubmit} className="space-y-4 mt-2">
                        <div className="space-y-1">
                            <Label htmlFor="create-name">{t('superadmin.company_name', 'Company Name')}</Label>
                            <Input
                                id="create-name"
                                value={createForm.name}
                                onChange={handleCreateNameChange}
                                placeholder="Acme AB"
                                autoFocus
                            />
                            {createErrors.name && (
                                <p className="text-xs text-red-600">{createErrors.name}</p>
                            )}
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="create-org-number">
                                {t('superadmin.org_number', 'Org Number')}
                                <span className="text-red-500 ml-0.5">*</span>
                            </Label>
                            <Input
                                id="create-org-number"
                                value={createForm.org_number}
                                onChange={handleOrgNumberChange}
                                placeholder="556000-0000"
                            />
                            {createErrors.org_number && (
                                <p className="text-xs text-red-600">{createErrors.org_number}</p>
                            )}
                            <p className="text-xs text-gray-500">
                                {t('superadmin.org_number_slug_hint', 'Slug is auto-derived from org number. Edit if needed.')}
                            </p>
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="create-slug">{t('superadmin.slug', 'Slug (ID)')}</Label>
                            <Input
                                id="create-slug"
                                value={createForm.slug}
                                onChange={handleCreateFieldChange('slug')}
                                placeholder="556000000"
                                className="font-mono text-sm"
                            />
                            {createErrors.slug && (
                                <p className="text-xs text-red-600">{createErrors.slug}</p>
                            )}
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="create-owner-email">{t('superadmin.owner_email', 'Owner Email')}</Label>
                            <Input
                                id="create-owner-email"
                                type="email"
                                value={createForm.owner_email}
                                onChange={handleCreateFieldChange('owner_email')}
                                placeholder="owner@acme.se"
                            />
                            {createErrors.owner_email && (
                                <p className="text-xs text-red-600">{createErrors.owner_email}</p>
                            )}
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="create-owner-name">{t('superadmin.owner_name', 'Owner Name')}</Label>
                            <Input
                                id="create-owner-name"
                                value={createForm.owner_name}
                                onChange={handleCreateFieldChange('owner_name')}
                                placeholder="Anna Svensson"
                            />
                            {createErrors.owner_name && (
                                <p className="text-xs text-red-600">{createErrors.owner_name}</p>
                            )}
                        </div>
                        <DialogFooter className="pt-2">
                            <Button type="button" variant="outline" onClick={handleCreateCancel}>
                                {t('common.cancel', 'Cancel')}
                            </Button>
                            <Button type="submit" disabled={creating}>
                                {creating
                                    ? t('common.saving', 'Saving…')
                                    : t('superadmin.create_company', 'Create Company')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Temp Password Dialog */}
            <Dialog
                open={tempPasswordDialog.open}
                onOpenChange={(open) => {
                    if (!open) setTempPasswordDialog({ open: false, password: '', company: '' });
                }}
            >
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>
                            {t('superadmin.company_created', 'Company Created')}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        <p className="text-sm text-gray-600">
                            {t(
                                'superadmin.company_created_desc',
                                '"{{company}}" has been created. Share this temporary password with the owner — they must change it on first login.',
                                { company: tempPasswordDialog.company }
                            )}
                        </p>
                        <div className="space-y-1">
                            <Label>{t('superadmin.temp_password', 'Temporary Password')}</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    readOnly
                                    value={tempPasswordDialog.password}
                                    className="font-mono text-sm"
                                />
                                <button
                                    onClick={copyTempPassword}
                                    className="flex items-center gap-1 text-xs px-3 py-2 rounded border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors whitespace-nowrap"
                                >
                                    <Copy className="size-3.5" />
                                    {tempPasswordCopied
                                        ? t('superadmin.copied', 'Copied!')
                                        : t('superadmin.copy', 'Copy')}
                                </button>
                            </div>
                        </div>
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                            {t(
                                'superadmin.temp_password_warning',
                                'This password will not be shown again. Copy it before closing.'
                            )}
                        </p>
                    </div>
                    <DialogFooter className="pt-2">
                        <Button
                            onClick={() => setTempPasswordDialog({ open: false, password: '', company: '' })}
                        >
                            {t('common.close', 'Close')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Test Accounts Section */}
            <div className="mt-10">
                <div className="mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <FlaskConical className="size-5 text-amber-600" strokeWidth={1.5} />
                        {t('superadmin.test_accounts', 'Test Accounts')}
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {t('superadmin.test_flag_note', 'Test accounts can be impersonated by super admin for UI testing. They should contain no real personal data.')}
                    </p>
                </div>
                <Card>
                    <CardContent className="p-0">
                        {usersLoading ? (
                            <div className="flex items-center justify-center py-16 text-sm text-gray-500">
                                {t('common.loading', 'Loading')}…
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 bg-gray-50">
                                            <th className="text-left px-4 py-3 font-medium text-gray-600">
                                                {t('common.name', 'Name')}
                                            </th>
                                            <th className="text-left px-4 py-3 font-medium text-gray-600">
                                                {t('common.email', 'Email')}
                                            </th>
                                            <th className="text-left px-4 py-3 font-medium text-gray-600">
                                                {t('common.role', 'Role')}
                                            </th>
                                            <th className="text-left px-4 py-3 font-medium text-gray-600">
                                                {t('superadmin.company_name', 'Company')}
                                            </th>
                                            <th className="text-left px-4 py-3 font-medium text-gray-600">
                                                {t('superadmin.test_accounts', 'Test Account')}
                                            </th>
                                            <th className="text-left px-4 py-3 font-medium text-gray-600">
                                                {t('common.actions', 'Actions')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allUsers.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">
                                                    {t('superadmin.no_test_accounts', 'No test accounts yet. Mark users as test accounts above.')}
                                                </td>
                                            </tr>
                                        ) : (
                                            allUsers.map((u) => (
                                                <tr
                                                    key={u.id}
                                                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                                                >
                                                    <td className="px-4 py-3 font-medium text-gray-900">{u.name}</td>
                                                    <td className="px-4 py-3 text-gray-600">{u.email}</td>
                                                    <td className="px-4 py-3 text-gray-500 text-xs uppercase tracking-wide">{u.role}</td>
                                                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{u.company_id || '—'}</td>
                                                    <td className="px-4 py-3">
                                                        {u.is_test_account ? (
                                                            <span className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 w-fit">
                                                                <FlaskConical className="size-3" />
                                                                Test
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400 text-xs">—</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handleToggleTestFlag(u.id)}
                                                                disabled={togglingTestFlag === u.id}
                                                                className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded border transition-colors disabled:opacity-50 ${
                                                                    u.is_test_account
                                                                        ? 'border-amber-200 text-amber-700 hover:bg-amber-50'
                                                                        : 'border-gray-200 text-gray-600 hover:bg-gray-100'
                                                                }`}
                                                            >
                                                                <FlaskConical className="size-3.5" />
                                                                {togglingTestFlag === u.id
                                                                    ? t('common.updating', 'Updating…')
                                                                    : u.is_test_account
                                                                        ? t('superadmin.unmark_test_account', 'Remove test flag')
                                                                        : t('superadmin.mark_test_account', 'Mark as test')}
                                                            </button>
                                                            {u.is_test_account && (
                                                                <button
                                                                    onClick={() => handleImpersonate(u.id)}
                                                                    disabled={impersonating === u.id}
                                                                    className="flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-violet-200 text-violet-700 hover:bg-violet-50 transition-colors disabled:opacity-50"
                                                                >
                                                                    <LogIn className="size-3.5" />
                                                                    {impersonating === u.id
                                                                        ? t('common.loading', 'Loading…')
                                                                        : t('superadmin.impersonate', 'Impersonate')}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Edit Company Dialog */}
            <Dialog
                open={editState.open}
                onOpenChange={(open) => { if (!open) handleEditCancel(); }}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{t('superadmin.edit_company_title', 'Edit Company')}</DialogTitle>
                    </DialogHeader>
                    {editState.company && (
                        <form onSubmit={handleEditSubmit} className="space-y-4 mt-2">
                            {/* Read-only identifiers */}
                            <div className="space-y-2 rounded-md border border-gray-100 bg-gray-50 p-3 text-xs text-gray-500">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium min-w-[90px]">{t('superadmin.edit_slug_readonly', 'Slug (Tenant ID)')}</span>
                                    <span className="font-mono text-gray-700">{editState.company.slug}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-medium min-w-[90px]">{t('superadmin.edit_id_readonly', 'Internal ID')}</span>
                                    <span className="font-mono text-gray-700">{editState.company.id}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-medium min-w-[90px]">{t('superadmin.edit_created_at_readonly', 'Created')}</span>
                                    <span className="text-gray-700">{formatDate(editState.company.created_at)}</span>
                                </div>
                            </div>

                            {/* Editable fields */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1 col-span-2">
                                    <Label htmlFor="edit-name">{t('superadmin.edit_name', 'Company Name')}</Label>
                                    <Input
                                        id="edit-name"
                                        value={editState.form.name}
                                        onChange={handleEditFieldChange('name')}
                                        placeholder="Acme AB"
                                        autoFocus
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="edit-org-number">{t('superadmin.edit_org_number', 'Org Number')}</Label>
                                    <Input
                                        id="edit-org-number"
                                        value={editState.form.org_number}
                                        onChange={handleEditFieldChange('org_number')}
                                        placeholder="556000-0000"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="edit-vat-number">{t('superadmin.edit_vat_number', 'VAT Number')}</Label>
                                    <Input
                                        id="edit-vat-number"
                                        value={editState.form.vat_number}
                                        onChange={handleEditFieldChange('vat_number')}
                                        placeholder="SE556000000001"
                                    />
                                </div>
                                <div className="space-y-1 col-span-2">
                                    <Label htmlFor="edit-legal-address">{t('superadmin.edit_legal_address', 'Legal Address')}</Label>
                                    <Input
                                        id="edit-legal-address"
                                        value={editState.form.legal_address}
                                        onChange={handleEditFieldChange('legal_address')}
                                        placeholder="Storgatan 1, 123 45 Stockholm"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="edit-phone">{t('superadmin.edit_phone', 'Phone')}</Label>
                                    <Input
                                        id="edit-phone"
                                        value={editState.form.phone}
                                        onChange={handleEditFieldChange('phone')}
                                        placeholder="+46 8 123 456"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="edit-email">{t('superadmin.edit_email', 'Email')}</Label>
                                    <Input
                                        id="edit-email"
                                        type="email"
                                        value={editState.form.email}
                                        onChange={handleEditFieldChange('email')}
                                        placeholder="info@acme.se"
                                    />
                                </div>
                                <div className="space-y-1 col-span-2">
                                    <Label htmlFor="edit-website">{t('superadmin.edit_website', 'Website')}</Label>
                                    <Input
                                        id="edit-website"
                                        value={editState.form.website}
                                        onChange={handleEditFieldChange('website')}
                                        placeholder="https://acme.se"
                                    />
                                </div>
                            </div>

                            <DialogFooter className="pt-2">
                                <Button type="button" variant="outline" onClick={handleEditCancel} disabled={editState.saving}>
                                    {t('common.cancel', 'Cancel')}
                                </Button>
                                <Button type="submit" disabled={editState.saving}>
                                    {editState.saving
                                        ? t('common.saving', 'Saving…')
                                        : t('superadmin.edit_save', 'Save Changes')}
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteState.open}
                onOpenChange={(open) => { if (!open) handleDeleteCancel(); }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-red-700">
                            {t('superadmin.delete_company', 'Delete Company')}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        <p className="text-sm text-gray-700">
                            {t(
                                'superadmin.delete_confirm_desc',
                                'You are about to permanently delete "{{name}}". This action cannot be undone.',
                                { name: deleteState.name }
                            )}
                        </p>

                        {deleteState.dryRunLoading ? (
                            <div className="text-sm text-gray-500 py-2">
                                {t('superadmin.checking_impact', 'Checking deletion impact…')}
                            </div>
                        ) : deleteState.dryRunResult ? (
                            <div className="bg-red-50 border border-red-200 rounded p-3 space-y-1">
                                <p className="text-xs font-semibold text-red-800 uppercase tracking-wide">
                                    {t('superadmin.will_delete', 'Records that will be deleted')}
                                </p>
                                {Object.entries(deleteState.dryRunResult).map(([key, value]) =>
                                    key !== 'company' ? (
                                        <p key={key} className="text-sm text-red-700">
                                            <span className="font-medium capitalize">{key.replace(/_/g, ' ')}</span>: {value}
                                        </p>
                                    ) : null
                                )}
                            </div>
                        ) : null}
                    </div>
                    <DialogFooter className="pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleDeleteCancel}
                            disabled={deleteState.executing}
                        >
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteExecute}
                            disabled={deleteState.dryRunLoading || deleteState.executing}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            {deleteState.executing
                                ? t('common.deleting', 'Deleting…')
                                : t('superadmin.delete_company', 'Delete Company')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
